import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import FourOhFour from "../errors/four-oh-four";
import hljs from "highlight.js";
import "highlight.js/styles/github.css";
import { getSingleExperience } from "./functions";
import { codeTemplate } from "./constants";
import { Texture } from "./interfaces";
import useAnalytics from "../hooks/useAnalytics";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const getQuerySearch = (
  lang = "",
  title = true,
  topSelected: string[],
  sidesSelected: string[],
  expTextures: { top: Texture[]; sides: Texture[] },
) => {
  let paramQuery = new URLSearchParams();
  if (lang) {
    paramQuery.set("lang", lang);
  }
  if (!title) {
    paramQuery.set('notitle', 'true');
  }
  if (topSelected.length !== expTextures.top.length) {
    paramQuery.set("only_top", topSelected.join(","));
  }
  if (sidesSelected.length !== expTextures.sides.length) {
    paramQuery.set("only_sides", sidesSelected.join(","));
  }
  return paramQuery.size > 0 ? `?${paramQuery.toString()}` : "";
};

export interface IExperienceSingleProps {
  companyName: string;
}

const ExperienceSingle = ({
  companyName,
}: IExperienceSingleProps): JSX.Element => {
  const { experienceID } = useParams();
  const query = useQuery();
  const [lang, setLang] = useState(query.get("lang") || "en");
  const [showTitle, setShowTitle] = useState(true);
  const experience = getSingleExperience(companyName, experienceID);
  // const embedAddress =
  //   process.env.NODE_ENV === "development"
  //     ? "http://localhost:4173"
  //     : "https://jg-embed.brand3d.com";
  const embedAddress = "https://jg-embed.brand3d.com";
  const [sidesSelected, setSidesSelected] = useState<string[]>(
    experience
      ? (experience.textures.sides.map(
          (t: Texture): string => t.id,
        ) as string[])
      : ([] as string[]),
  );
  const [topSelected, setTopSelected] = useState<string[]>(
    experience
      ? (experience.textures.top.map((t: Texture): string => t.id) as string[])
      : ([] as string[]),
  );

  // Re-highlight the code box each time the code updates
  useEffect(() => {
    const codeElement = document.getElementById("brand3d-embed-code");
    if (codeElement) {
      codeElement.removeAttribute("data-highlighted");
      hljs.highlightElement(codeElement);
    }
  }, [topSelected, sidesSelected, lang, showTitle]);
  document.title = experience ? experience.name : "404 Not found";
  useAnalytics();
  if (experience) {
    return (
      <div className="content">
        <h1>{experience.name}</h1>
        {/* START OF 3D EXPERIENCE */}
        <iframe
          title="Brand3D Embedded Experience"
          src={`${embedAddress}/${experience.id}${getQuerySearch(
            lang,
            showTitle,
            topSelected,
            sidesSelected,
            experience.textures,
          )}`}
          seamless
          width="100%"
          height="700px"
          allow="clipboard-read; clipboard-write"
          className="brand3d-embedded-iframe"
        ></iframe>
        {/* END OF 3D EXPERIENCE */}
        {/* START OF EMBED CODE BOX */}
        <hr />
        <h2>Embed code:</h2>
        <pre>
          <code aria-readonly id="brand3d-embed-code">
            {codeTemplate
              .replace("%embed_address%", embedAddress)
              .replace("%id%", experience.id)
              .replace(
                "%options%",
                getQuerySearch(
                  lang,
                  showTitle,
                  topSelected,
                  sidesSelected,
                  experience.textures,
                ),
              )}
          </code>
        </pre>
        {/* END OF EMBED CODE BOX */}
        <hr />
        {/* START OF FINISH OPTIONS FOR VENDORS */}
        <div className="brand3d-embed-options-select">
          <h3>Finish Options for Vendors</h3>
          <p>
            You can enable and disable different options to reflect availability
            in your inventory.
          </p>
          {/* START OF SHELL OPTIONS */}
          <h4>Shell Options</h4>
          <div className="brand3d-finish-options">
            {experience.textures.top.map((tex: Texture): JSX.Element => {
              return (
                <div className="brand3d-texture-selector" key={tex.id}>
                  <label className="brand3d-finish-option" htmlFor={tex.id}>
                    <input
                      type="checkbox"
                      id={tex.id}
                      checked={topSelected.includes(tex.id)}
                      value={tex.id}
                      onChange={(evt: ChangeEvent<HTMLInputElement>): void => {
                        const { value, checked } = evt.target;
                        setTopSelected(
                          topSelected.includes(value) && topSelected.length > 1
                            ? topSelected
                                .filter((sel: string): boolean => sel !== value)
                                .filter((e, i, a) => a.indexOf(e) === i)
                            : topSelected
                                .concat([value])
                                .filter((e, i, a) => a.indexOf(e) === i),
                        );
                        if (!checked && topSelected.length === 1) {
                          alert(
                            "You can't remove all options, at least one option must be selected.",
                          );
                        }
                      }}
                    />{" "}
                    {tex.name}
                  </label>
                </div>
              );
            })}
          </div>
          {/* END OF SHELL OPTIONS */}
          {/* START OF CABINET OPTIONS */}
          <h4>Cabinet Options</h4>
          <div className="brand3d-finish-options">
            {experience.textures.sides.map((tex: Texture): JSX.Element => {
              return (
                <div className="brand3d-texture-selector" key={tex.id}>
                  <label htmlFor={tex.id}>
                    <input
                      type="checkbox"
                      id={tex.id}
                      checked={sidesSelected.includes(tex.id)}
                      value={tex.id}
                      onChange={(evt: ChangeEvent<HTMLInputElement>): void => {
                        const { value, checked } = evt.target;
                        setSidesSelected(
                          sidesSelected.includes(value) &&
                            sidesSelected.length > 1
                            ? sidesSelected
                                .filter((sel: string): boolean => sel !== value)
                                .filter((e, i, a) => a.indexOf(e) === i)
                                : sidesSelected
                                .concat([value])
                                .filter((e, i, a) => a.indexOf(e) === i),
                        );
                        if (!checked && sidesSelected.length === 1) {
                          alert(
                            "You can't remove all options, at least one option must be selected.",
                          );
                        }
                      }}
                    />{" "}
                    {tex.name}
                  </label>
                </div>
              );
            })}
          </div>
          {/* END OF CABINET OPTIONS */}
        </div>
        <hr />
        {/* END OF FINISH OPTIONS FOR VENDORS */}
        {/* START LANGUAGE OPTIONS */}
        <p>
          <label htmlFor="language-selector">
            <strong>Language:</strong>&nbsp;
            <select name="" id="" onChange={(event) => {
              setLang(event.target.value)
            }} value={lang}>
              <option value="en">English (default)</option>
              <option value="fr-ca">French (Canada)</option>
              <option value="fr-fr">French (France)</option>
              <option value="no">Norwegian</option>
              <option value="es">Spanish</option>
              <option value="nl">Dutch</option>
            </select>
          </label>
        </p>
        <hr />
        {/* END LANGUAGE OPTIONS */}
        {/* START TITLE OPTIONS */}
        <p>
          <label htmlFor="show-title">
            <input type="checkbox" id="show-title" name="show-title" checked={showTitle} onChange={() => {
              setShowTitle(!showTitle);
            }} />
            <strong>Show Title</strong> <small>(On by default)</small>
          </label>
        </p>
        {/* END TITLE OPTIONS */}
        {/* START OF NOTES */}
        <div className="brand3d-embed-code-notes">
          <h4>Notes:</h4>
          <ul>
            <li>
              The embed in this page has a white background for clarity
              purposes,{" "}
              <i>the copied code does NOT include this white background.</i>
            </li>
            <li>
              The CSS snippet included in the embed code is completely optional;
              but it's a good place to start testing customizations to the
              embed.
            </li>
          </ul>
        </div>
        {/* END OF NOTES */}
      </div>
    );
  } else {
    return <FourOhFour />;
  }
};

export default ExperienceSingle;
